import { cx } from "../../lib/cx.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import { Button } from "../Button/Button.js";
import { Table, TableBody, TableCell, TableHead, TableRow } from "../Table/Table.js";
/* empty css          */
import { useId, useLayoutEffect, useRef, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/components/Chart/Chart.tsx
var MAX_SERIES = 4;
var PAD = {
	top: 12,
	right: 12,
	bottom: 28,
	left: 48
};
var BAR_GAP = 2;
var FALLBACK_WIDTH = 600;
var compact = new Intl.NumberFormat("en-US", {
	notation: "compact",
	maximumFractionDigits: 1
});
var defaultFormat = (value) => compact.format(value);
/** A round step (1, 1.5, 2, 2.5, 3, 4, 5, 6 or 8 × 10ⁿ): the first that covers `max` in `ticks` steps. */
function niceMax(max, ticks) {
	if (max <= 0) return ticks;
	const raw = max / ticks;
	const magnitude = 10 ** Math.floor(Math.log10(raw));
	return [
		1,
		1.5,
		2,
		2.5,
		3,
		4,
		5,
		6,
		8,
		10
	].find((f) => f * magnitude >= raw) * magnitude * ticks;
}
/** The container's width, kept current. Without a ResizeObserver (jsdom), a fixed one. */
function useWidth() {
	const ref = useRef(null);
	const [width, setWidth] = useState(FALLBACK_WIDTH);
	useLayoutEffect(() => {
		const element = ref.current;
		if (!element || typeof ResizeObserver === "undefined") return;
		const measure = () => setWidth(Math.max(160, Math.round(element.clientWidth)));
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(element);
		return () => observer.disconnect();
	}, []);
	return [ref, width];
}
/** A column chart's top: square at the baseline, 4px round at the data end. */
function barPath(x, y, w, h) {
	const r = Math.min(4, w / 2, h);
	return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}
/**
* A dependency-free SVG chart: line, area or grouped columns over categories, with a
* hover tooltip, a legend for two or more series, a text summary and the data as a
* table. One y-axis, from zero. For a trend in a tile, use Sparkline.
*/
function Chart({ type = "line", categories, series: seriesProp, title, summary, height = 220, ticks = 4, valueFormatter = defaultFormat, showGrid = true, showLegend, defaultShowTable = false, emptyLabel = "No data to show", className }) {
	const id = useId();
	const [plotRef, width] = useWidth();
	const [hover, setHover] = useState(null);
	const [showTable, setShowTable] = useState(defaultShowTable);
	const series = seriesProp.slice(0, MAX_SERIES);
	const empty = categories.length === 0 || series.every((s) => s.data.length === 0);
	const legend = showLegend ?? series.length > 1;
	const plotW = width - PAD.left - PAD.right;
	const plotH = height - PAD.top - PAD.bottom;
	const max = niceMax(Math.max(0, ...series.flatMap((s) => s.data)), ticks);
	const y = (value) => PAD.top + plotH * (1 - Math.max(0, value) / max);
	const slot = plotW / Math.max(1, categories.length);
	const x = (index) => PAD.left + slot * (index + .5);
	const generated = summary ?? (empty ? "" : `${typeof title === "string" ? `${title}, ` : ""}${type} chart, ${categories[0]} to ${categories.at(-1)}. ` + series.map((s) => {
		const values = s.data.filter((v) => Number.isFinite(v));
		if (values.length === 0) return `${s.name}: no data.`;
		return `${s.name}: from ${valueFormatter(values[0])} to ${valueFormatter(values.at(-1))}, highest ${valueFormatter(Math.max(...values))}.`;
	}).join(" "));
	const handlePointer = (event) => {
		const box = event.currentTarget.getBoundingClientRect();
		const ratio = box.width > 0 ? (event.clientX - box.left) / box.width : 0;
		const index = Math.floor(ratio * categories.length);
		setHover(Math.min(categories.length - 1, Math.max(0, index)));
	};
	const titleId = `${id}-title`;
	const groupW = Math.min(slot * .7, series.length * 26);
	const barW = Math.max(1, groupW / series.length - BAR_GAP);
	return /* @__PURE__ */ jsxs("figure", {
		className: cx("ds-chart", className),
		"aria-labelledby": titleId,
		children: [
			/* @__PURE__ */ jsxs("figcaption", {
				className: "ds-chart__header",
				children: [/* @__PURE__ */ jsx("span", {
					id: titleId,
					className: "ds-chart__title",
					children: title
				}), !empty && /* @__PURE__ */ jsx(Button, {
					variant: "ghost",
					size: "sm",
					"aria-expanded": showTable,
					onClick: () => setShowTable(!showTable),
					children: showTable ? "Hide table" : "Show table"
				})]
			}),
			!empty && /* @__PURE__ */ jsx(VisuallyHidden, { children: generated }),
			legend && !empty && /* @__PURE__ */ jsx("ul", {
				className: "ds-chart__legend",
				"aria-hidden": "true",
				children: series.map((s, index) => /* @__PURE__ */ jsxs("li", {
					className: "ds-chart__legend-item",
					children: [/* @__PURE__ */ jsx("span", { className: `ds-chart__swatch ds-chart--s${index + 1}` }), s.name]
				}, s.name))
			}),
			/* @__PURE__ */ jsxs("div", {
				ref: plotRef,
				className: "ds-chart__plot",
				children: [empty ? /* @__PURE__ */ jsx("p", {
					className: "ds-chart__empty",
					style: { blockSize: height },
					children: emptyLabel
				}) : /* @__PURE__ */ jsxs("svg", {
					className: "ds-chart__svg",
					width,
					height,
					viewBox: `0 0 ${width} ${height}`,
					"aria-hidden": "true",
					children: [
						Array.from({ length: ticks + 1 }, (_, t) => {
							const value = max * t / ticks;
							return /* @__PURE__ */ jsxs("g", { children: [(showGrid || t === 0) && /* @__PURE__ */ jsx("line", {
								className: t === 0 ? "ds-chart__baseline" : "ds-chart__grid",
								x1: PAD.left,
								x2: width - PAD.right,
								y1: y(value),
								y2: y(value)
							}), /* @__PURE__ */ jsx("text", {
								className: "ds-chart__axis",
								x: PAD.left - 8,
								y: y(value),
								dy: "0.32em",
								textAnchor: "end",
								children: valueFormatter(value)
							})] }, t);
						}),
						categories.map((category, index) => /* @__PURE__ */ jsx("text", {
							className: "ds-chart__axis",
							x: x(index),
							y: height - PAD.bottom + 18,
							textAnchor: "middle",
							children: category
						}, category)),
						hover !== null && type !== "bar" && /* @__PURE__ */ jsx("line", {
							className: "ds-chart__crosshair",
							x1: x(hover),
							x2: x(hover),
							y1: PAD.top,
							y2: y(0)
						}),
						type === "bar" ? series.map((s, si) => /* @__PURE__ */ jsx("g", {
							className: `ds-chart--s${si + 1}`,
							children: s.data.map((value, index) => {
								const left = x(index) - groupW / 2 + si * (barW + BAR_GAP) + BAR_GAP / 2;
								const top = y(value);
								return /* @__PURE__ */ jsx("path", {
									className: cx("ds-chart__bar", hover !== null && hover !== index && "ds-chart__bar--dim"),
									d: barPath(left, top, barW, Math.max(0, y(0) - top))
								}, index);
							})
						}, s.name)) : series.map((s, si) => {
							const points = s.data.map((value, index) => `${x(index)},${y(value)}`).join(" ");
							const last = s.data.length - 1;
							return /* @__PURE__ */ jsxs("g", {
								className: `ds-chart--s${si + 1}`,
								children: [
									type === "area" && /* @__PURE__ */ jsx("polygon", {
										className: "ds-chart__area",
										points: `${x(0)},${y(0)} ${points} ${x(last)},${y(0)}`
									}),
									/* @__PURE__ */ jsx("polyline", {
										className: "ds-chart__line",
										points
									}),
									(hover === null ? [last] : [hover]).map((index) => s.data[index] === void 0 ? null : /* @__PURE__ */ jsx("circle", {
										className: "ds-chart__dot",
										cx: x(index),
										cy: y(s.data[index]),
										r: 4
									}, index))
								]
							}, s.name);
						}),
						/* @__PURE__ */ jsx("rect", {
							className: "ds-chart__hit",
							x: PAD.left,
							y: PAD.top,
							width: plotW,
							height: plotH,
							onPointerMove: handlePointer,
							onPointerDown: handlePointer,
							onPointerLeave: () => setHover(null)
						})
					]
				}), hover !== null && !empty && /* @__PURE__ */ jsxs("div", {
					className: "ds-chart__tooltip",
					"aria-hidden": "true",
					style: {
						insetInlineStart: Math.min(x(hover) + 12, width - 160),
						insetBlockStart: PAD.top
					},
					children: [/* @__PURE__ */ jsx("span", {
						className: "ds-chart__tooltip-title",
						children: categories[hover]
					}), series.map((s, si) => /* @__PURE__ */ jsxs("span", {
						className: "ds-chart__tooltip-row",
						children: [
							/* @__PURE__ */ jsx("span", { className: `ds-chart__swatch ds-chart--s${si + 1}` }),
							/* @__PURE__ */ jsx("span", { children: s.name }),
							/* @__PURE__ */ jsx("span", {
								className: "ds-chart__tooltip-value",
								children: s.data[hover] === void 0 ? "–" : valueFormatter(s.data[hover])
							})
						]
					}, s.name))]
				})]
			}),
			showTable && !empty && /* @__PURE__ */ jsxs(Table, {
				"aria-label": typeof title === "string" ? `${title}, data` : "Chart data",
				children: [/* @__PURE__ */ jsxs(TableHead, { children: [/* @__PURE__ */ jsx(TableCell, { children: "Category" }), series.map((s) => /* @__PURE__ */ jsx(TableCell, {
					numeric: true,
					children: s.name
				}, s.name))] }), /* @__PURE__ */ jsx(TableBody, { children: categories.map((category, index) => /* @__PURE__ */ jsxs(TableRow, { children: [/* @__PURE__ */ jsx(TableCell, {
					rowHeader: true,
					label: "Category",
					children: category
				}), series.map((s) => /* @__PURE__ */ jsx(TableCell, {
					numeric: true,
					label: s.name,
					children: s.data[index] === void 0 ? "–" : valueFormatter(s.data[index])
				}, s.name))] }, category)) })]
			})
		]
	});
}
/**
* A small trend line for a stat tile or a table cell: no axes, the last value marked.
* Decorative unless it has a `label`.
*/
function Sparkline({ data, tone = "primary", width = 120, height = 32, area = false, endDot = true, label, className }) {
	const pad = 5;
	const min = Math.min(...data);
	const span = Math.max(...data) - min;
	const x = (index) => data.length <= 1 ? width / 2 : pad + (width - 10) * index / (data.length - 1);
	const y = (value) => span === 0 ? height / 2 : pad + (height - 10) * (1 - (value - min) / span);
	const points = data.map((value, index) => `${x(index)},${y(value)}`).join(" ");
	const last = data.length - 1;
	return /* @__PURE__ */ jsx("svg", {
		className: cx("ds-sparkline", `ds-sparkline--${tone}`, className),
		width,
		height,
		viewBox: `0 0 ${width} ${height}`,
		...label ? {
			role: "img",
			"aria-label": label
		} : { "aria-hidden": true },
		children: data.length > 0 && /* @__PURE__ */ jsxs(Fragment$1, { children: [
			area && /* @__PURE__ */ jsx("polygon", {
				className: "ds-sparkline__area",
				points: `${x(0)},${height - pad} ${points} ${x(last)},${height - pad}`
			}),
			/* @__PURE__ */ jsx("polyline", {
				className: "ds-sparkline__line",
				points
			}),
			endDot && /* @__PURE__ */ jsx("circle", {
				className: "ds-sparkline__dot",
				cx: x(last),
				cy: y(data[last]),
				r: 3
			})
		] })
	});
}
//#endregion
export { Chart, Sparkline };

//# sourceMappingURL=Chart.js.map